import React, { FC } from 'react';
import { CouponInfo } from '../../../../types/coupons';
import { ServiceData, SlotService } from '../../../../utils/state/types';
import {
  isFixedPrice,
  shouldDisplayPolicyWarning,
} from '../../../../utils/payment';
import { mapToArray } from '../../../../utils';
import {
  Checkbox,
  SectionNotification,
  SectionNotificationType,
} from 'wix-ui-tpa/cssVars';
import {
  useEnvironment,
  useExperiments,
  useTranslation,
} from '@wix/yoshi-flow-editor';
import { useFormActions } from '../../Hooks/useFormActions';
import { classes, st } from './CouponCheckbox.st.css';
import { CouponCheckboxDatahooks } from './datahooks';
import Text, { TextType } from '../../Text/Text';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import { PaymentMethod } from '../../../../types/types';
import { bookingsUoUBookingFormClickSrc16Evid83 } from '@wix/bi-logger-bookings-data/v2';
import {
  BookingFormClickActions,
  BookingFormClickComponents,
} from '../../../../types/biLoggerTypes';
import { ExperimentsConsts } from '../../../../consts/experiments';

export interface CouponCheckboxProps {
  isServiceInCart?: boolean;
  couponInfo: CouponInfo;
  serviceData: ServiceData;
}

export const CouponCheckbox: FC<CouponCheckboxProps> = ({
  isServiceInCart,
  couponInfo,
  serviceData,
}) => {
  const [checked, setChecked] = React.useState(
    couponInfo.isCouponCheckboxChecked,
  );
  const { isUpsellPluginInstalled } = useFormComponentContext();
  const { onCouponCheckboxCheck, logBi } = useFormActions();
  const { t } = useTranslation();
  const { isMobile } = useEnvironment();
  const { experiments } = useExperiments();

  // eslint-disable-next-line
  const handleCheckboxChange = (checked: boolean) => {
    setChecked(checked);
    onCouponCheckboxCheck(checked);
    logBi([
      bookingsUoUBookingFormClickSrc16Evid83({
        component: BookingFormClickComponents.PromoCode,
        action: BookingFormClickActions.Click,
        value: String(checked),
      }),
    ]);
  };

  const slotServices = mapToArray<SlotService>(serviceData.slotServices);

  const shouldShowCouponCheckbox =
    !shouldDisplayPolicyWarning({
      slotServices: serviceData.slotServices,
      isServiceInCart: isServiceInCart!,
      isUpsellPluginInstalled: isUpsellPluginInstalled!,
      isPricingPlanRefundPolicyEnabled: experiments.enabled(
        ExperimentsConsts.PricingPlanRefundPolicy,
      ),
    }) &&
    !isServiceInCart &&
    !isUpsellPluginInstalled &&
    couponInfo.areCouponsAvailable &&
    slotServices.some(
      (slotService) =>
        isFixedPrice(slotService.service.payment) &&
        [
          slotService.selectedPaymentType,
          // Multi-participant flow: any participant paying offline qualifies.
          ...(slotService.participants ?? []).map(
            (participant) => participant.payment?.selectedPaymentType,
          ),
        ].includes(PaymentMethod.OFFLINE),
    );

  if (!shouldShowCouponCheckbox) {
    return null;
  }

  return (
    <div className={st(classes.root, { isMobile })}>
      <Checkbox
        className={classes.checkbox}
        data-hook={CouponCheckboxDatahooks.Checkbox}
        checked={checked}
        label={t('app.bookings-form.coupon-checkbox.label')}
        // eslint-disable-next-line
      onChange={({ checked }) => handleCheckboxChange(checked)}
      />
      {checked && (
        <SectionNotification
          data-hook={CouponCheckboxDatahooks.SectionMessage}
          className={classes.message}
          type={SectionNotificationType.default}
        >
          <SectionNotification.Text>
            <Text type={TextType.Primary} isBlackOnly>
              {t('app.bookings-form.coupon-checkbox.section-message')}
            </Text>
          </SectionNotification.Text>
        </SectionNotification>
      )}
    </div>
  );
};
